import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageWrapper from '../components/PageWrapper';
import FileUpload from '../components/FileUpload';
import { useTranslation } from 'react-i18next';
import TrackMetadataForm from './trackDetail/TrackMetadataForm';
import type { Track } from '../services/trackTypes';
import { createTrack, uploadArtworkAsync, uploadAudio } from '../services/trackService';
import toast from 'react-hot-toast';
import { logInfo, logError } from '../services/logtail';
import { saveSessionData, getSessionData, clearSessionData } from '../utils/sessionRecovery';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useWebSocketNotifications } from '../hooks/useWebSocketNotifications';
import CassetteLoader from '../components/CassetteLoader';
import { SketchProgressBar } from '../components/sketchy';
import ga from '../services/analytics';
import StorageRequiredBanner from '../components/StorageRequiredBanner';
import { useStorageGate } from '../hooks/useStorageGate';

const defaultNewTrack = (): Track => ({
  id: '',
  name: '',
  releaseDate: '',
  createdDate: new Date().toISOString(),
  progress: 0,
  status: 'Draft',
  artist: '',
  isSingle: true,
  multilink: '',
});

const CreateTrack: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { canCreate } = useStorageGate();
  const [artwork, setArtwork] = useState<string | null>(null);
  const [track, setTrack] = useState<Track>(defaultNewTrack());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [audioUploadProgress, setAudioUploadProgress] = useState<number>(0);
  const [audioUploading, setAudioUploading] = useState<boolean>(false);
  const [uploadedAudio, setUploadedAudio] = useState<{ fileId: string, name: string } | null>(null);
  const [pendingAudioFile, setPendingAudioFile] = useState<File | null>(null);
  const [currentUploadId, setCurrentUploadId] = useState<string | null>(null);
  const [currentTrackId, setCurrentTrackId] = useState<string | null>(null);
  const [currentCreatingMessage, setCurrentCreatingMessage] = useState<string>('');

  // Use WebSocket notifications for audio upload completion
  useWebSocketNotifications({
    trackId: currentTrackId || '',
    fileType: 'audio',
    onUploadComplete: (notification) => {
      const { upload_id, file_id, filename } = notification;
      
      // Check if this is our current upload
      if (upload_id === currentUploadId && currentTrackId) {
        logInfo('Audio upload completed via WebSocket', { upload_id, file_id, filename });
        
        // Update the track with the uploaded audio info
        setTrack((prev: Track) => ({
          ...prev,
          audioFiles: [{ fileId: file_id || '', name: filename || pendingAudioFile?.name || 'Unknown file' }]
        }));
        
        // Set uploaded audio state for UI display
        setUploadedAudio({ 
          fileId: file_id || '', 
          name: filename || pendingAudioFile?.name || 'Unknown file' 
        });
        
        // Clear upload states
        setAudioUploading(false);
        setAudioUploadProgress(0);
        setCurrentUploadId(null);
        
        // Now complete the track creation process
        if (currentTrackId) {
          const trackToComplete = {
            ...track,
            id: currentTrackId,
            audioFiles: [{ fileId: file_id || '', name: filename || pendingAudioFile?.name || 'Unknown file' }]
          };
          completeTrackCreation(trackToComplete);
        }
      }
    },
    onUploadFailed: (notification) => {
      const { upload_id, error } = notification;
      
      // Check if this is our current upload
      if (upload_id === currentUploadId) {
        logError('Audio upload failed via WebSocket', { upload_id, error });
        
        // Clear upload states
        setAudioUploading(false);
        setAudioUploadProgress(0);
        setCurrentUploadId(null);
      }
    },
    onAuthIssue: (notification) => {
      logInfo('🔐 AUTH ISSUE: CreateTrack received auth issue notification', { notification });
    }
  });

  // Restore session data on mount
  useEffect(() => {
    const sessionData = getSessionData();
    if (sessionData && sessionData.page === '/tracks/new') {
      logInfo('Restoring session data for track creation', { dataKeys: Object.keys(sessionData.data) });
      if (sessionData.data.track) setTrack(sessionData.data.track);
      if (sessionData.data.artwork) setArtwork(sessionData.data.artwork);
      // Note: File objects can't be serialized, so audioFile won't be restored
      clearSessionData();
      toast.success('Your track data has been restored!');
    }
  }, []);

  // Rotating funny messages during track creation
  useEffect(() => {
    if (!isSubmitting) {
      setCurrentCreatingMessage('');
      return;
    }

    const messages = t('track.creatingMessages', { returnObjects: true }) as string[];
    if (!Array.isArray(messages) || messages.length === 0) {
      setCurrentCreatingMessage(t('track.creating'));
      return;
    }

    // Set initial message
    const randomIndex = Math.floor(Math.random() * messages.length);
    setCurrentCreatingMessage(messages[randomIndex]);

    // Rotate messages every 3.5 seconds
    const interval = setInterval(() => {
      setCurrentCreatingMessage(prevMessage => {
        const availableMessages = messages.filter(msg => msg !== prevMessage);
        if (availableMessages.length === 0) return prevMessage;
        
        const randomIndex = Math.floor(Math.random() * availableMessages.length);
        return availableMessages[randomIndex];
      });
    }, 3500);

    return () => clearInterval(interval);
  }, [isSubmitting, t]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default form submission
    if (!canCreate) return;

    setIsSubmitting(true); // Show loader immediately

    if (!track.name || !track.artist) {
      toast.error('Track name and artist are required.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Save session data before creating track
      saveSessionData('/tracks/new', {
        track,
        artwork
      });

      logInfo('Starting track creation process', { 
        hasArtwork: !!artwork, 
        hasAudioFile: !!pendingAudioFile,
        trackName: track.name 
      });
      
      // Create the track first
      const newTrack = await createTrack({
        ...track,
        artwork: undefined, // artwork upload is not supported at creation
        audioFiles: [], // audio file will be uploaded after track is created
        createdDate: new Date().toISOString(),
      });

      logInfo('Track created successfully', { trackId: newTrack.id });
      ga.event('track_created');

      // Upload audio if provided
      if (pendingAudioFile) {
        logInfo('Uploading audio file for track', { trackId: newTrack.id, fileName: pendingAudioFile.name });
        setAudioUploading(true);
        setCurrentTrackId(newTrack.id); // Set current track ID for WebSocket notifications
        
        try {
          const { uploadId, message } = await uploadAudio(newTrack.id, pendingAudioFile, (progress: number) => {
            setAudioUploadProgress(progress);
            logInfo('Audio upload progress', { trackId: newTrack.id, progress });
          });
          
          logInfo('Audio upload queued successfully', { trackId: newTrack.id, uploadId, message });
          
          // Set the upload ID to track via WebSocket
          setCurrentUploadId(uploadId);
          
          // Don't set uploadedAudio yet - wait for WebSocket completion
          // The UI will show "Uploading..." until WebSocket confirms completion
          
          // Wait for audio upload to complete before proceeding
          return; // Exit early, let WebSocket handle completion
          
        } catch (error) {
          logError('Failed to queue audio upload', { error: error instanceof Error ? error.message : String(error) });
          toast.error('Failed to queue audio upload. Please try again.');
          setAudioUploading(false);
          setCurrentTrackId(null);
          setCurrentUploadId(null);
          setIsSubmitting(false); // Re-enable button on error
          return;
        }
      }

      // If no audio to upload, proceed with final steps
      await completeTrackCreation(newTrack);
      
    } catch (error) {
      logError('Failed to create track', { 
        error: error instanceof Error ? error.message : String(error),
        trackName: track.name,
        hasArtwork: !!artwork,
        hasAudioFile: !!pendingAudioFile
      });
      
      // Check if this is a Google OAuth error - if so, the global handler will show the dialog
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.status === 401 && 
            (axiosError.response?.data?.errorMessage === 'google_oauth_expired' || 
             axiosError.response?.data?.errorMessage === 'google_oauth_invalid')) {
          // The global OAuth handler will show the proper dialog
          // Just reset the UI state and don't show generic error
          setIsSubmitting(false);
          return;
        }
        
        // For other errors, show the specific backend error message if available
        const backendMessage = axiosError.response?.data?.errorMessage;
        if (backendMessage) {
          // Check for specific error types
          if (backendMessage.includes('track limit reached')) {
            toast.error(t('track.trackLimitReached'));
          } else {
            toast.error(backendMessage);
          }
          setIsSubmitting(false);
          return;
        }
      }
      
      // Fallback to generic error
      toast.error('Failed to create track. Please try again.');
      setIsSubmitting(false); // Re-enable button on error
    }
  };

  // Helper function to complete track creation (called after audio upload completes)
  const completeTrackCreation = async (newTrack: Track) => {
    setIsSubmitting(true);
    
    try {
      // Upload artwork if provided
      if (artwork && artwork.startsWith('data:')) {
        logInfo('Uploading artwork for track', { trackId: newTrack.id });
        // Convert data URL to File object
        const response = await fetch(artwork);
        const blob = await response.blob();
        const artworkFile = new File([blob], 'artwork.jpg', { type: 'image/jpeg' });
        
        await uploadArtworkAsync(newTrack.id, artworkFile, (progress) => {
          logInfo('Artwork upload progress', { trackId: newTrack.id, progress });
        });
        logInfo('Artwork upload completed', { trackId: newTrack.id });
      }

      logInfo('Track creation process completed successfully', { trackId: newTrack.id });
      clearSessionData(); // Clear session data on successful creation
      toast.success('Track created successfully!');
      navigate(`/tracks/${newTrack.id}`, { state: { fromCreate: true }, replace: true });
    } catch (error) {
      logError('Failed to complete track creation', { 
        error: error instanceof Error ? error.message : String(error),
        trackId: newTrack.id
      });
      toast.error('Failed to complete track creation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageWrapper 
      title={t('track.create')}
      subtitle={t('track.createSubtitle')}
    >
      <div className="rounded-xl shadow-sm border p-6" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
        {!canCreate && (
          <div className="mb-6">
            <StorageRequiredBanner />
          </div>
        )}
        <form className="flex flex-col gap-6" onSubmit={handleCreate}>
          <TrackMetadataForm 
            value={track} 
            onChange={(updates) => setTrack((prev: Track) => ({ ...prev, ...updates }))} 
          />
          
          <div className="pt-6" style={{ borderTop: '1px solid var(--color-border)' }}>
            <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--color-text)' }}>{t('track.uploadDemo')}</h3>
            <p className="text-sm mb-4" style={{ color: 'var(--color-muted)' }}>
              {t('track.demoUploadHint')}
            </p>
            <FileUpload
              label={t('track.uploadDemo')}
              fileCategory="audio"
              onFile={files => {
                const file = Array.isArray(files) ? files[0] : files?.[0] || null;
                if (!file) return;
                setUploadedAudio(null);
                setAudioUploadProgress(0);
                setAudioUploading(false);
                setPendingAudioFile(file);
              }}
              uploadProgress={audioUploadProgress}
              uploading={audioUploading}
              onRemove={() => {
                setUploadedAudio(null);
                setAudioUploadProgress(0);
                setAudioUploading(false);
                setPendingAudioFile(null);
              }}
            />
            
            {/* Show upload progress */}
            {pendingAudioFile && audioUploadProgress > 0 && audioUploadProgress < 100 && (
                              <div className="mt-4 p-4 border rounded-lg" style={{ backgroundColor: 'var(--color-hover)', borderColor: 'var(--color-border)' }}>
                <div className="flex items-center gap-3 mb-2">
                  <CassetteLoader size={48} variant="inline" />
                  <span className="text-sm font-medium text-blue-800">Uploading {pendingAudioFile.name}...</span>
                </div>
                <SketchProgressBar value={audioUploadProgress} data-testid="create-track-audio-progress" />
                <div className="text-xs text-blue-600 mt-1">{audioUploadProgress}%</div>
              </div>
            )}
            
            {/* Show uploaded file info */}
            {uploadedAudio && (
                              <div className="mt-4 flex items-center justify-between border rounded-lg px-4 py-3" style={{ backgroundColor: 'var(--color-hover)', borderColor: 'var(--color-border)' }}>
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  <span className="text-green-800 font-medium">{uploadedAudio.name}</span>
                </div>
                <button
                  type="button"
                  className="text-red-500 hover:text-red-700 text-xs font-medium"
                  onClick={() => { 
                    setUploadedAudio(null); 
                    setAudioUploadProgress(0); 
                    setPendingAudioFile(null);
                  }}
                >
                  {t('buttons.remove')}
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="py-2 px-5 rounded-lg shadow-sm text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition"
              style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }}
              data-testid="cancel-button"
            >
              {t('buttons.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary py-2 px-5 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting || audioUploading || !canCreate}
              data-testid="track-create-button"
            >
              {isSubmitting && !audioUploading ? t('track.creating') : 
               audioUploading ? 'Uploading Audio...' : 
               t('track.create')}
            </button>
          </div>
        </form>
      </div>
      
      {isSubmitting && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'var(--modal-backdrop)' }}>
          <div className="rounded-lg p-8 flex flex-col items-center border" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
            <CassetteLoader size={100} />
            <div className="mt-4 text-sm font-medium" style={{ color: 'var(--color-muted)' }}>
              {currentCreatingMessage || t('track.creating')}
            </div>
          </div>
        </div>
      )}
    </PageWrapper>
  );
};

export default CreateTrack; 